import i18n from '@/i18n';
import { ApiError } from './api';

const KNOWN_ERROR_CODES = new Set([
  'VALIDATION_ERROR',
  'UNAUTHORIZED',
  'FORBIDDEN',
  'NOT_FOUND',
  'CONFLICT',
  'INSUFFICIENT_STOCK',
  'RATE_LIMITED',
  'INTERNAL_ERROR',
  'INTERNAL_CONSISTENCY',
  'PARSE_ERROR',
  // The server could not be reached to renew the session. Client-raised, not a
  // server code — the session is intact and retrying is the right move.
  'NETWORK_ERROR',
  // Only reaches the user if a re-issued token comes back stale a second time,
  // which means something is wrong rather than merely out of date.
  'NOT_A_MEMBER',
  // A platform operator switched this clinic off. Like NOT_A_MEMBER, `api.ts`
  // usually redirects to the picker before anyone renders this — it reaches the
  // user only where that redirect cannot help.
  'CLINIC_DISABLED',
]);

// Codes whose server-side `message` typically carries case-specific detail the
// user needs (e.g. "This would leave the clinic with nobody able to manage
// roles", "Only 3 available in this batch"). For these, prefer the server text;
// for everything else, prefer the localized message keyed by code. Update this
// set as the backend evolves.
//
// FORBIDDEN is here because authorization refusals stopped being a flat "no".
// "Cannot grant permissions you do not hold: analytics.read" tells someone
// exactly which checkbox to clear; the generic string tells them nothing and
// leaves them stuck. The trade-off is that these particular messages are
// English-only — better than untranslated but actionable turning into
// translated but useless.
const SURFACE_SERVER_DETAIL_CODES = new Set(['CONFLICT', 'INSUFFICIENT_STOCK', 'FORBIDDEN']);

interface TranslateOptions {
  /** Override for when the value is not an Error / ApiError. */
  fallback?: string;
}

/**
 * Translate an API error (or any unknown thrown value) into a user-facing
 * message in the current language.
 *
 * Strategy:
 *   - ApiError with a known code in SURFACE_SERVER_DETAIL_CODES → server message
 *     (when non-empty), else the translated code message.
 *   - ApiError with any other known code → translated `errors.<CODE>`.
 *   - ApiError with an unknown code → server message, else translated fallback.
 *   - Plain Error → its message.
 *   - Anything else → caller-supplied fallback, else translated fallback.
 *
 * Backend messages are currently English-only. Routing through translation by
 * default means non-English users get localized text for routine errors; the
 * CONFLICT exception keeps detail-bearing collision messages visible.
 */
export function translateApiError(err: unknown, opts: TranslateOptions = {}): string {
  const t = i18n.getFixedT(i18n.language);
  if (err instanceof ApiError) {
    const detail = err.message?.trim() ?? '';
    if (KNOWN_ERROR_CODES.has(err.code)) {
      if (SURFACE_SERVER_DETAIL_CODES.has(err.code) && detail.length > 0) {
        return detail;
      }
      return t(`errors.${err.code}` as const);
    }
    return detail.length > 0 ? detail : t('errors.fallback');
  }
  if (err instanceof Error && err.message) return err.message;
  return opts.fallback ?? t('errors.fallback');
}
